/** @jsx jsx */
// eslint-disable-next-line
import React from 'react'
import { css, jsx } from '@emotion/core'
import styled from '@emotion/styled'
import { IconButton, InputAdornment } from '@material-ui/core'
import { Visibility, VisibilityOff } from '@material-ui/icons'
import Button from '@material-ui/core/Button'
import axios from 'axios'
import { Formik, Field } from 'formik'
import { isValidPhoneNumber } from 'libphonenumber-js'
import PropTypes from 'prop-types'
import ReactFlagsSelect from 'react-flags-select'
import { toast } from 'react-toastify'
import AutocompleteField from './AutocompleteField'
import errorMessage from '../../../utils/errorMessage'
import Amplitude from '../../../analytics/amplitude'
import phoneCodes from '../../../constants/phoneCodes.json'
import * as Constants from '../../../constants'
import * as UserUtils from '../../../utils/user'
import { getPasswordErrors } from '../../../utils/passwordPolicy'
import sleep from '../../../utils/sleep'
import * as Translations from '../../../translations'
import MetricaGreenTextField from './MetricaGreenTextField'
import PasswordRequirements from './PasswordRequirements'

const styles = {}

styles.form = css`
  width: 100%;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  gap: 4px;

  & .MuiFormControl-marginNormal {
    margin-top: 10px;
    margin-bottom: 8px;
    position: relative;
  }
  & .MuiOutlinedInput-input {
    padding-top: 13px;
    padding-bottom: 13px;
  }
  & .MuiAutocomplete-inputRoot[class*='MuiOutlinedInput-root'] {
    padding-top: 3.5px;
    padding-bottom: 3.5px;
  }
  & .MuiFormHelperText-root {
    position: absolute;
    top: 100%;
    right: 0;
    margin: 2px 0 0;
    font-size: 11px;
    line-height: 1.2;
    text-align: right;
  }
`

styles.createContainer = css`
  margin-top: 30px;
  text-decoration: none;
  align-items: center;
  z-index: 10;
  @media (max-width: 700px) {
    justify-content: center;
  },
`

styles.sendButton = {
  minWidth: 260,
  width: '100%',
  paddingTop: 13,
  textTransform: 'none',
  paddingBottom: 13,
  borderRadius: '8px',
  margin: '0 auto 10px auto',
  display: 'block'
}

styles.inputContainer = css`
  display: flex;
  justify-content: space-between;
  color: ${Constants.theme.palette.text.main};
  @media (max-width: 1230px) {
    flex-direction: column;
  }
`

styles.inputWrapper = css`
  flex: 1;
  padding: 0 10px;
  @media (max-width: 700px) {
    margin-bottom: 10px;
  }
`

styles.passwordRequirements = css`
  padding: 0 24px;
  margin: -10px 0 -7px;
  @media (max-width: 700px) {
    margin: -20px 0 3px;
  }
`

styles.seeTerms = css`
  min-width: 260px;
  text-align: center;
  width: 100%;
  margin: 0 auto;
  color: ${Constants.theme.palette.text.secondary};
  & a {
    color: ${Constants.theme.palette.text.secondary};
  }
`

styles.downloadLink = css`
  font-weight: bolder;
  font-size: 14px;
  cursor: default;
  &:hover {
    text-decoration: underline;
  }
`

styles.selectFlag = css`
  display: flex;
  flex-direction: row;
  justify-content: center;
`

const FlagContainer = styled.div`
  width: 100%;

  .flagContainer {
    display: flex;
    align-items: normal;
    margin-bottom: 3px;
    width: 100%;
  }
`

const initialValues = {
  name: '',
  lastName: '',
  email: '',
  phoneCode: '',
  phone: '',
  clubCompany: '',
  userKey: '',
  job: '',
  sport: '',
  password: '',
  password2: '',
  showPassword: false,
  termsAndConditions: true
}

function CreateUserForm(props) {
  const [selected, setSelected] = React.useState('')
  const [countries, setCountries] = React.useState({})
  const [language, setLanguage] = React.useState(props.language)
  const [jobOptions, setJobOptions] = React.useState([])
  const [sportOptions, setSportOptions] = React.useState([])
  const isMobile = window.innerWidth < 500

  React.useEffect(() => {
    initialValues.email = props.email || ''

    getCountries()
    getJobOptions()
    getSportOptions()
  }, [])

  React.useEffect(() => {
    if (countries && Object.keys(countries).length > 0) {
      getUserCountry()
    }
  }, [countries])

  React.useEffect(() => {
    getJobOptions()
    getSportOptions()
    setLanguage(props.language)
  }, [props.language])

  function getCountries() {
    setCountries(phoneCodes)
  }

  async function getUserCountry() {
    const res = await axios.get('https://geolocation-db.com/json/')
    if (res.data) {
      selectPhoneCode(res.data.country_code)
    }
  }

  function selectPhoneCode(code) {
    setSelected(code)
    initialValues.phoneCode = countries[code].secondary
  }

  function getJobOptions() {
    const options = Constants.Jobs.map(job => ({
      value: job,
      label: window.I18n.t(`jobs.${job}`)
    })).sort((a, b) => {
      if (a.value === 'other') {
        return 1
      } else if (b.value === 'other') {
        return -1
      }

      return a.label.localeCompare(b.label)
    })

    setJobOptions(options)
  }

  function getSportOptions() {
    const options = Constants.Sports.map(sport => ({
      value: sport,
      label: window.I18n.t(`sports.${sport}`)
    })).sort((a, b) => {
      if (a.value === 'other') {
        return 1
      } else if (b.value === 'other') {
        return -1
      }

      return a.label.localeCompare(b.label)
    })

    setSportOptions(options)
  }

  return (
    <Formik
      initialValues={initialValues}
      validate={validateForm(selected)}
      onSubmit={onSubmit(props)}
    >
      {formik => (
        <form
          noValidate
          autoComplete="off"
          css={styles.form}
          onSubmit={formik.handleSubmit}
          method="POST"
        >
          <input autoComplete="false" name="hidden" type="text" style={{ display: 'none' }} />
          <div css={styles.inputContainer}>
            <div css={styles.inputWrapper} onScroll={onInputScroll}>
              <MetricaGreenTextField
                label={`${window.I18n.t('createUser.name')}*`}
                placeholder={window.I18n.t('createUser.name')}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.name}
                type="text"
                name="name"
                inputProps={{ maxLength: Constants.FieldMaxLengths.name }}
                margin="normal"
                variant="outlined"
                error={formik.errors.name && formik.touched.name && !!formik.errors.name}
                helperText={formik.errors.name && formik.touched.name && formik.errors.name}
                fullWidth
              />
            </div>
            <div css={styles.inputWrapper}>
              <MetricaGreenTextField
                label={`${window.I18n.t('createUser.lastName')}*`}
                placeholder={window.I18n.t('createUser.lastName')}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.lastName}
                type="text"
                name="lastName"
                inputProps={{ maxLength: Constants.FieldMaxLengths.lastName }}
                margin="normal"
                variant="outlined"
                error={
                  formik.errors.lastName && formik.touched.lastName && !!formik.errors.lastName
                }
                helperText={
                  formik.errors.lastName && formik.touched.lastName && formik.errors.lastName
                }
                fullWidth
              />
            </div>
          </div>
          <div css={styles.inputWrapper}>
            <MetricaGreenTextField
              label={`${window.I18n.t('createUser.email')}*`}
              placeholder={window.I18n.t('createUser.email')}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.email}
              type="email"
              name="email"
              inputProps={{ maxLength: Constants.FieldMaxLengths.email }}
              margin="normal"
              variant="outlined"
              error={formik.errors.email && formik.touched.email && !!formik.errors.email}
              helperText={formik.errors.email && formik.touched.email && formik.errors.email}
              fullWidth
            />
          </div>
          <div css={styles.inputContainer}>
            <div css={styles.inputWrapper}>
              <MetricaGreenTextField
                label={`${window.I18n.t('createUser.password')}*`}
                placeholder={window.I18n.t('createUser.password')}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.password}
                type={formik.values.showPassword ? 'text' : 'password'}
                name="password"
                margin="normal"
                variant="outlined"
                error={
                  formik.errors.password && formik.touched.password && !!formik.errors.password
                }
                helperText={
                  !formik.values.password &&
                  formik.errors.password &&
                  formik.touched.password &&
                  formik.errors.password
                }
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" css={{ position: 'absolute', right: '9px' }}>
                      {(formik.values.password || formik.values.password2) && (
                        <IconButton
                          name="showPassword"
                          aria-label="toggle password visibility"
                          onClick={onToggleShowPassword(formik)}
                          size="small"
                          style={{ color: 'rgba(255, 255, 255, 0.6)' }}
                        >
                          {formik.values.showPassword ? (
                            <Visibility fontSize="small" />
                          ) : (
                            <VisibilityOff fontSize="small" />
                          )}
                        </IconButton>
                      )}
                    </InputAdornment>
                  )
                }}
              />
            </div>
            <div css={styles.inputWrapper}>
              <MetricaGreenTextField
                label={`${window.I18n.t('createUser.repeatPassword')}*`}
                placeholder={window.I18n.t('createUser.repeatPassword')}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.password2}
                type={formik.values.showPassword ? 'text' : 'password'}
                name="password2"
                margin="normal"
                variant="outlined"
                error={
                  !hasPendingPasswordRequirements(formik) &&
                  formik.errors.password2 &&
                  formik.touched.password2 &&
                  !!formik.errors.password2
                }
                helperText={
                  !hasPendingPasswordRequirements(formik) &&
                  formik.errors.password2 &&
                  formik.touched.password2 &&
                  formik.errors.password2
                }
                fullWidth
              />
            </div>
          </div>
          {hasPendingPasswordRequirements(formik) && (
            <div css={styles.passwordRequirements}>
              <PasswordRequirements
                password={formik.values.password}
                color={props.product.primaryMain}
              />
            </div>
          )}
          <div css={styles.inputWrapper}>
            <Field
              id="job"
              name="job"
              key={`job-${language}`}
              component={AutocompleteField}
              options={jobOptions}
              error={formik.errors.job && formik.touched.job && !!formik.errors.job}
              helperText={formik.errors.job && formik.touched.job && formik.errors.job}
              textFieldProps={{
                fullWidth: true,
                margin: 'normal',
                variant: 'outlined',
                label: `${window.I18n.t('createUser.job')}*`,
                placeholder: window.I18n.t('createUser.job')
              }}
            />
          </div>
          <div css={styles.inputWrapper}>
            <Field
              id="sport"
              name="sport"
              key={`sport-${language}`}
              component={AutocompleteField}
              options={sportOptions}
              error={formik.errors.sport && formik.touched.sport && !!formik.errors.sport}
              helperText={formik.errors.sport && formik.touched.sport && formik.errors.sport}
              color="secondary"
              textFieldProps={{
                fullWidth: true,
                margin: 'normal',
                variant: 'outlined',
                label: `${window.I18n.t('createUser.sport')}*`,
                placeholder: window.I18n.t('createUser.sport')
              }}
            />
          </div>
          <div css={styles.inputContainer}>
            <div css={styles.inputWrapper} style={{ minWidth: '45%' }}>
              <MetricaGreenTextField
                label={`${window.I18n.t('createUser.clubCompany')}*`}
                placeholder={window.I18n.t('createUser.clubCompany')}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.clubCompany}
                type="text"
                name="clubCompany"
                inputProps={{ maxLength: Constants.FieldMaxLengths.clubCompany }}
                margin="normal"
                variant="outlined"
                error={
                  formik.errors.clubCompany &&
                  formik.touched.clubCompany &&
                  !!formik.errors.clubCompany
                }
                helperText={
                  formik.errors.clubCompany &&
                  formik.touched.clubCompany &&
                  formik.errors.clubCompany
                }
                fullWidth
              />
            </div>
            <div
              css={{
                ...styles.inputWrapper,
                ...styles.selectFlag
              }}
              style={{
                minWidth: '55%',
                alignItems: 'flex-start'
              }}
            >
              <div
                css={styles.inputWrapper}
                style={{
                  ...(!isMobile && { minWidth: '46%' })
                }}
              >
                <FlagContainer>
                  <ReactFlagsSelect
                    className="flagContainer"
                    selected={selected}
                    searchable
                    searchPlaceholder={window.I18n.t('createUser.searchPhoneCode')}
                    onSelect={code => selectPhoneCode(code)}
                    customLabels={countries}
                    placeholder={window.I18n.t('createUser.phoneCode')}
                    selectedSize={16}
                    css={css`
                      height: 45px;
                      margin-top: 10px;
                      width: 100%;
                    `}
                  />
                </FlagContainer>
              </div>
              <div
                css={styles.inputWrapper}
                style={{
                  ...(!isMobile && { minWidth: '54%' })
                }}
              >
                <MetricaGreenTextField
                  label={`${window.I18n.t('createUser.phone')}*`}
                  placeholder={window.I18n.t('createUser.phone')}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.phone}
                  type="tel"
                  name="phone"
                  inputProps={{ maxLength: Constants.FieldMaxLengths.phone }}
                  margin="normal"
                  variant="outlined"
                  error={formik.errors.phone && formik.touched.phone && !!formik.errors.phone}
                  helperText={formik.errors.phone && formik.touched.phone && formik.errors.phone}
                  fullWidth
                />
              </div>
            </div>
          </div>
          <div css={styles.inputContainer}>
            <div css={styles.inputWrapper}>
              <MetricaGreenTextField
                label={`${window.I18n.t('createUser.userKeyField')}`}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.userKey}
                type="text"
                name="userKey"
                inputProps={{ maxLength: Constants.FieldMaxLengths.userKey }}
                margin="normal"
                variant="standard"
                error={formik.errors.userKey && formik.touched.userKey && !!formik.errors.userKey}
                helperText={
                  formik.errors.userKey && formik.touched.userKey && formik.errors.userKey
                }
                fullWidth
              />
            </div>
          </div>
          <div css={styles.createContainer}>
            <Button
              variant="contained"
              color="primary"
              style={{
                ...styles.sendButton,
                background: props.product.buttonBackground,
                color: props.product.buttonColor
              }}
              disabled={formik.isSubmitting}
              type="submit"
            >
              {formik.isSubmitting
                ? window.I18n.t('createUser.submitting')
                : window.I18n.t('createUser.proceed')}
            </Button>
            <p
              css={styles.seeTerms}
              dangerouslySetInnerHTML={{
                __html: window.I18n.t('createUser.signAgreement', {
                  pre1: '<a href="https://metrica-sports.com/privacy-policy/">',
                  post1: '</a>',
                  pre2: '<a href="https://metrica-sports.com/terms-conditions/">',
                  post2: '</a>'
                })
              }}
            />
          </div>
        </form>
      )}
    </Formik>
  )
}

function validateForm(selected) {
  return values => {
    const errors = {}
    if (!values.name) {
      errors.name = window.I18n.t('common.required')
    }

    if (!values.lastName) {
      errors.lastName = window.I18n.t('common.required')
    }

    if (!values.email) {
      errors.email = window.I18n.t('common.required')
    }

    if (!values.clubCompany) {
      errors.clubCompany = window.I18n.t('common.required')
    }

    if (!values.job) {
      errors.job = window.I18n.t('common.required')
    }

    if (!values.sport) {
      errors.sport = window.I18n.t('common.required')
    }

    if (!values.phone) {
      errors.phone = window.I18n.t('common.required')
    } else {
      const isValid = isValidPhoneNumber(values.phone, selected)
      if (!isValid) {
        errors.phone = window.I18n.t('common.required')
      }
    }

    if (values.email && !Constants.emailRegex.test(values.email)) {
      errors.email = window.I18n.t('createUser.invalidEmail')
    }
    validatePass(values, errors)

    return errors
  }
}

function validatePass(values, errors) {
  if (values.password && getPasswordErrors(values.password).length > 0) {
    errors.password = window.I18n.t('createUser.passwordInvalid')
  }

  if (values.password && !values.password2) {
    errors.password2 = window.I18n.t('common.required')
    return
  }

  if (values.password2 && !values.password) {
    errors.password = window.I18n.t('common.required')
    return
  }

  if (values.password !== values.password2) {
    errors.password2 = window.I18n.t('createUser.passwordsDontMatch')
    return
  }

  if (!values.password) {
    errors.password = window.I18n.t('common.required')
  }

  if (!values.password2) {
    errors.password2 = window.I18n.t('common.required')
  }
}

function hasPendingPasswordRequirements(formik) {
  return !!formik.values.password && !!formik.errors.password
}

function onToggleShowPassword(formik) {
  return () => {
    formik.setFieldValue('showPassword', !formik.values.showPassword)
  }
}

function onSubmit(props) {
  return async (values, { setSubmitting }) => {
    try {
      const payload = createPayload(values, props)
      const response = await props.createUserAsync(payload)
      const user = response.data

      try {
        Amplitude.setUser(user)
        await Amplitude.logEvent(Constants.AnalyticsEvents.TRIAL_USER_CREATED)
        await window.MetricaSportsAnalytics.track(Constants.AnalyticsEventTypes.USER_CREATED)
      } catch (error) {
        console.log('Error logging analytics', error)
      }

      // Add referral tracking to First-Promoter.
      try {
        const fprData = { email: user.email }
        if (user.payment_ids[0]) {
          fprData.uid = user.payment_ids[0].customer
        }

        window.fpr('referral', fprData)

        // NOTE: Make sure it doesn't redirect the form instantly, otherwise the redirection
        // can block any ongoing requests, including the tracking one for First-Promoter.
        await sleep(500)
      } catch (error) {
        console.log('Error adding referral', error)
      }

      // Redirect to proper URL.
      if (props.invitationToken) {
        openInvitationPage(
          props.product,
          props.invitationToken,
          props.invitationFrom,
          props.invitationTo
        )
      } else if (props.returnUrl) {
        openReturnUrl(props.returnUrl)
      } else {
        openDownloadsApp(user, props.product)
      }
    } catch (e) {
      if (errorMessage(e).includes(Constants.emailErrorResponse)) {
        toast.error(({ closeToast }) => {
          return (
            <div onClick={closeToast}>
              <span>{`${window.I18n.t('error.emailTaken')}.`}</span>
              <br />
              <span onClick={downloadLink} css={styles.downloadLink}>
                {`${window.I18n.t('error.downloadPlay')}.`}
              </span>
            </div>
          )
        })
      } else if (errorMessage(e).includes(Constants.userKeyResponse)) {
        toast.error(({ closeToast }) => {
          return <div onClick={closeToast}>{`${window.I18n.t('error.userKey')}.`}</div>
        })
      } else if (errorMessage(e).includes(Constants.passwordPolicyErrorResponse)) {
        toast.error(window.I18n.t('createUser.passwordInvalid'))
      } else if (errorMessage(e).includes(Constants.fieldLengthErrorResponse)) {
        toast.error(window.I18n.t('error.fieldTooLong'))
      } else {
        toast.error(window.I18n.t('error.tryAgain'))
      }
    } finally {
      setSubmitting(false)
      values = initialValues
    }
  }
}

function downloadLink() {
  const href = Constants.downloadsAppUrl[process.env.REACT_APP_ENV]
  const link = Object.assign(document.createElement('a'), { href })
  link.click()
}

function createPayload(values, props) {
  const payload = {
    email: values.email,
    password: values.password,
    name: values.name,
    lastName: values.lastName,
    company: values.clubCompany,
    job: Translations.English.phrases.jobs[values.job.value] || values.job.value,
    sport: Translations.English.phrases.sports[values.sport.value] || values.sport.value,
    termsAndConditions: values.termsAndConditions,
    language: props.language,
    app: props.product.app
  }

  if (values.phoneCode) {
    payload.phoneCode = values.phoneCode
  }

  if (values.phone) {
    payload.phone = values.phone
  }

  if (values.userKey) {
    payload.userKey = values.userKey
  }

  if (props.referral) {
    payload.referral = props.referral
  }

  if (props.token) {
    payload.token = props.token
  }

  return payload
}

function onInputScroll(e) {
  e.stopPropagation()
}

function openReturnUrl(returnUrl) {
  const link = Object.assign(document.createElement('a'), { href: returnUrl, target: '_self' })
  link.click()
}

function openDownloadsApp(user, product) {
  const queryParams = [
    `licence=${UserUtils.getLicence(user)}`,
    `organization=${UserUtils.getOrganization(user)}`,
    `id=${user._id}`,
    `email_notifications=${user.email_notifications}`,
    `name=${user.name}`,
    `lastUpdate=${user.lastUpdate}`,
    `last_login=${user.last_login}`,
    `email=${user.email}`,
    `referral=${user.referral}`,
    `language=${user.language}`,
    `company=${user.company}`,
    `job=${user.job}`,
    `sport=${user.sport}`,
    `app=${product.app}`
  ].join('&')

  // Prepend the product segment to the downloads path (e.g. /from-trial ->
  // /nexus/from-trial) via URL parsing so we don't depend on a magic substring.
  const url = new URL(Constants.downloadsAppUrl[process.env.REACT_APP_ENV])
  url.pathname = `/${product.id}${url.pathname}`
  const href = `${url.origin}${url.pathname}?${queryParams}`
  const link = Object.assign(document.createElement('a'), { href, target: '_self' })
  link.click()
}

function openInvitationPage(product, token, from, to) {
  const queryParams = [
    `invitationToken=${token}`,
    `invitationFrom=${encodeURIComponent(from)}`,
    `invitationTo=${encodeURIComponent(to)}`
  ].join('&')

  const href = `${Constants.getPlayAppUrl[process.env.REACT_APP_ENV]}/${product.id}/invitation?${queryParams}`
  const link = Object.assign(document.createElement('a'), { href, target: '_self' })
  link.click()
}

CreateUserForm.propTypes = {
  createUserAsync: PropTypes.func.isRequired,
  product: PropTypes.object.isRequired,
  email: PropTypes.string,
  language: PropTypes.string.isRequired,
  referral: PropTypes.string,
  token: PropTypes.string,
  returnUrl: PropTypes.string,
  invitationToken: PropTypes.string,
  invitationFrom: PropTypes.string,
  invitationTo: PropTypes.string
}

CreateUserForm.defaultProps = {
  email: '',
  referral: undefined,
  token: undefined,
  returnUrl: undefined,
  invitationToken: undefined,
  invitationFrom: '',
  invitationTo: ''
}

export default CreateUserForm
